'use client'

import { Popover } from '@/ui/popover/Popover.client'
import { Autocomplete, ListBox, useFilter } from 'react-aria-components'
import { isSection } from '../_utils/selectValue'
import { SelectListHeader } from './SelectListHeader.client'
import { SelectListItem, type SelectListItemData } from './SelectListItem.client'
import { SelectListSection, type SelectListSectionData } from './SelectListSection.client'
import { SelectSearchField } from './SelectSearchField.client'

type SelectPopoverProps = {
  items: SelectListItemData[] | SelectListSectionData[]
  maxHeight?: number
  isSearchable?: boolean
  searchPlaceholder: string
  searchAriaLabel: string
  clearAriaLabel: string
  noResultsLabel: string
  headerMessage?: string
}

/**
 * Height must flow from the Popover (RAC clamps max-height to the viewport) down to the
 * ListBox. Search and header stay outside the scrollport, not sticky inside it.
 */
export function SelectPopover({
  items,
  maxHeight,
  isSearchable = false,
  searchPlaceholder,
  searchAriaLabel,
  clearAriaLabel,
  noResultsLabel,
  headerMessage,
}: SelectPopoverProps) {
  const { contains } = useFilter({ sensitivity: 'base' })

  const listBox = (
    <ListBox
      items={items}
      className="min-h-0 w-full flex-1 overflow-y-auto outline-none"
      renderEmptyState={() => (
        <div className="flex h-6 items-center justify-center px-2 text-md text-brand-2-500">
          {noResultsLabel}
        </div>
      )}
    >
      {(item) =>
        isSection(item) ? <SelectListSection section={item} /> : <SelectListItem item={item} />
      }
    </ListBox>
  )

  const body = (
    <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
      {headerMessage ? <SelectListHeader message={headerMessage} /> : null}
      {isSearchable ? (
        <SelectSearchField
          placeholder={searchPlaceholder}
          aria-label={searchAriaLabel}
          clearAriaLabel={clearAriaLabel}
        />
      ) : null}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{listBox}</div>
    </div>
  )

  return (
    <Popover
      variant="default"
      maxHeight={maxHeight}
      className="flex w-[--trigger-width] flex-col overflow-hidden"
    >
      {isSearchable ? <Autocomplete filter={contains}>{body}</Autocomplete> : body}
    </Popover>
  )
}
